/**
 * Handles importing validated flight patterns into the database
 * and generating the rolling daily cache.
 */

import { supabase } from "@/integrations/supabase/client";
import type { ValidatedFlightPatternRow, ScheduleHeader } from "@/utils/scheduleMarkdownParser";
import { sanitiseTime } from "@/utils/timeUtils";

export type ImportStep = 'idle' | 'patterns' | 'cache' | 'logging' | 'maintenance' | 'done' | 'error';

export interface ImportProgress {
  step: ImportStep;
  message: string;
  patternsImported?: number;
  cacheRowsGenerated?: number;
  rowsPruned?: number;
}


const PNG_UTC_OFFSET_HOURS = 10;

/**
 * Build a UTC datetime string from a local date (YYYY-MM-DD) and time (HH:MM) in PNG timezone (UTC+10).
 */
function toUtcDatetime(dateStr: string, timeStr: string): string {
  const [h, m] = timeStr.split(':').map(Number);
  const dt = new Date(`${dateStr}T${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:00+10:00`);
  return dt.toISOString();
}

/**
 * Get the day-of-week bit index for a given date. 0=Sun, 1=Mon, ... 6=Sat.
 */
function getDayBit(date: Date): number {
  return date.getDay(); // JS getDay: 0=Sun, 1=Mon, ... 6=Sat — matches our bitmask
}

/**
 * Format a Date as YYYY-MM-DD.
 */
function formatDateStr(d: Date): string {
  return d.toISOString().split('T')[0];
}

/**
 * Add days to a date.
 */
function addDays(d: Date, n: number): Date {
  const result = new Date(d);
  result.setDate(result.getDate() + n);
  return result;
}

function extractValidToFromVersionName(versionName: string): string | null {
  const monthMap: Record<string, string> = {
    jan: '01', feb: '02', mar: '03', apr: '04', may: '05', jun: '06',
    jul: '07', aug: '08', sep: '09', oct: '10', nov: '11', dec: '12'
  };

  const match = versionName.match(/([a-zA-Z]+)(\d{1,2})?(\d{4})(?!.*\d{4})/i);
  if (!match) return null;

  const month = monthMap[match[1].slice(0, 3).toLowerCase()];
  const day = match[2] ? match[2].padStart(2, '0') : '01';
  const year = match[3];

  if (!month) return null;
  return `${year}-${month}-${day}`;
}

function calculateBitmask(days: string[]): number {
  const dayMap: Record<string, number> = {
    'Sun': 1, 'Sunday': 1,
    'Mon': 2, 'Monday': 2,
    'Tue': 4, 'Tuesday': 4,
    'Wed': 8, 'Wednesday': 8,
    'Thu': 16, 'Thursday': 16,
    'Fri': 32, 'Friday': 32,
    'Sat': 64, 'Saturday': 64,
  };
  return days.reduce((bitmask, day) => {
    const trimmed = day.trim();
    return bitmask | (dayMap[trimmed] ?? 0);
  }, 0);
}

function deduplicateRows(rows: ValidatedFlightPatternRow[], scheduleVersion: string): ValidatedFlightPatternRow[] {
  const uniqueMap = new Map<string, ValidatedFlightPatternRow>();
  const daysMap = new Map<string, string[]>();
  
  for (const row of rows) {
    const key = `${scheduleVersion}|${row.flight_number}|${row.dep_airport}|${row.dep_time}`;
    
    if (uniqueMap.has(key)) {
      // Merge bitmask via OR
      const existing = uniqueMap.get(key)!;
      existing.days_bitmask = existing.days_bitmask | row.days_bitmask;
      // Collect day display strings for recalculation
      const existingDays = daysMap.get(key) || [];
      if (row.days_display) {
        existingDays.push(...row.days_display.split(/[,\s/]+/).filter(Boolean));
      }
      daysMap.set(key, existingDays);
    } else {
      uniqueMap.set(key, { ...row });
      if (row.days_display) {
        daysMap.set(key, row.days_display.split(/[,\s/]+/).filter(Boolean));
      }
    }
  }

  // Apply calculateBitmask from merged day names as fallback when bitmask is 0
  let logCount = 0;
  for (const [key, mergedRow] of uniqueMap.entries()) {
    const mergedDays = daysMap.get(key) || [];
    if (mergedRow.days_bitmask === 0 && mergedDays.length > 0) {
      mergedRow.days_bitmask = calculateBitmask(mergedDays);
    }
    if (logCount < 5) {
      console.log(`Row ${mergedRow.flight_number} days: ${mergedDays.join(',')} → bitmask: ${mergedRow.days_bitmask}`);
      logCount++;
    }
  }

  const deduplicated = Array.from(uniqueMap.values());
  const duplicateCount = rows.length - deduplicated.length;
  console.log(`Deduplication: ${rows.length} raw rows → ${deduplicated.length} unique patterns (${duplicateCount} day values merged)`);
  return deduplicated;
}

export interface HandledFlightInfo {
  handled_code: string;
  handled_name: string;
  count: number;
}

export async function importSchedule(
  validRows: ValidatedFlightPatternRow[],
  header: ScheduleHeader,
  airlineCode: string,
  scheduleVersion: string,
  onProgress: (progress: ImportProgress) => void,
  fileHash?: string,
  scheduleType: 'seasonal' | 'daily' = 'seasonal',
  handledFlightsIncluded?: HandledFlightInfo[]
): Promise<ImportProgress> {
  try {
    // ─── Step 0a: Filter out invalid flight number rows ───
    const filteredRows = validRows.filter(row =>
      row.flight_number &&
      row.flight_number.trim() !== '' &&
      !['EMPTY', 'empty', '-', 'N/A'].includes(row.flight_number.trim())
    );
    console.log(`Import step 0a: Filtered ${validRows.length} → ${filteredRows.length} valid flight numbers (${validRows.length - filteredRows.length} removed)`);

    // ─── Step 0b: Deduplicate by conflict key ───
    const uniqueRows = deduplicateRows(filteredRows, scheduleVersion);
    console.log(`Import step 0b: Deduplicated to ${uniqueRows.length} unique rows`);

    // ─── Step 0c: Resolve valid_from and valid_to with fallbacks ───
    let validFrom = header.valid_from;
    let validTo = header.valid_to;

    // Guard: valid_from must not be empty
    if (!validFrom) {
      throw new Error('Could not determine valid_from date. Please check the PDF header or enter a date manually.');
    }

    // Fallback: extract valid_to from version name if missing or same as valid_from
    if (!validTo || validTo === validFrom) {
      const extracted = extractValidToFromVersionName(scheduleVersion);
      if (extracted) {
        console.log(`valid_to fallback: extracted "${extracted}" from version name "${scheduleVersion}"`);
        validTo = extracted;
      }
    }

    if (!validTo) {
      throw new Error('Could not determine valid_to date. Please check the schedule version name or PDF header.');
    }

    console.log('Resolved schedule dates — valid_from:', validFrom, 'valid_to:', validTo);

    // ─── Step 1: Upsert flight_patterns ───
    console.log(`Import step 1: Starting upsert, row count: ${uniqueRows.length}`);
    onProgress({ step: 'patterns', message: '⏳ Importing flight patterns...' });

    const toDbTime = (value: string | null | undefined): string | null => {
      if (!value) return null;
      if (value === 'TBA' || value === '....' || value === '…') return null;
      if (/^\d{2}:\d{2}$/.test(value)) return value;
      return null;
    };

    const patternPayloads = uniqueRows.map(row => {
      let depTime = toDbTime(sanitiseTime(row.dep_time) || row.dep_time);
      let arrTime = toDbTime(sanitiseTime(row.arr_time) || row.arr_time);

      if (depTime === null && arrTime !== null) {
        depTime = '00:00';
      }

      return {
        airline_code: airlineCode,
        schedule_version: scheduleVersion,
        flight_number: row.flight_number,
        dep_airport: row.dep_airport,
        dep_time: depTime,
        arr_airport: row.arr_airport,
        arr_time: arrTime,
        days_bitmask: row.days_bitmask,
        aircraft_type: row.aircraft_type || null,
        notes: row.notes || null,
        valid_from: validFrom,
        valid_to: validTo,
        imported_at: new Date().toISOString(),
        file_hash: fileHash || null,
      };
    });

    // Upsert in batches of 50
    const BATCH_SIZE = 50;
    const upsertedPatternIds: number[] = [];

    for (let i = 0; i < patternPayloads.length; i += BATCH_SIZE) {
      const batch = patternPayloads.slice(i, i + BATCH_SIZE);
      console.log(`Import step 1: Upserting batch ${Math.floor(i / BATCH_SIZE) + 1} (${batch.length} rows)`);
      const { data, error } = await supabase
        .from('flight_patterns')
        .upsert(batch as any, {
          onConflict: 'schedule_version,flight_number,dep_airport,dep_time',
        })
        .select('id');

      if (error) {
        console.error('flight_patterns upsert failed:', error);
        throw new Error(`Pattern upsert failed: ${error.message}`);
      }
      if (data) {
        upsertedPatternIds.push(...data.map((d: any) => d.id));
      }
    }

    const patternsImported = upsertedPatternIds.length;
    console.log(`Import step 1: Complete — ${patternsImported} patterns upserted`);
    onProgress({
      step: 'patterns',
      message: `✅ ${patternsImported} flight patterns saved`,
      patternsImported,
    });

    // ─── Step 2: Generate flight_daily_cache ───
    console.log('Import step 2: Starting cache generation');
    onProgress({ step: 'cache', message: '⏳ Generating daily cache...' });

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    let rangeStart: Date;
    let rangeEnd: Date;

    if (scheduleType === 'daily') {
      rangeStart = new Date(header.valid_from + 'T00:00:00');
      rangeEnd = new Date(header.valid_to + 'T00:00:00');
    } else {
      rangeStart = addDays(today, -14);
      rangeEnd = addDays(today, 30);
    }

    // Delete existing cache for this schedule_version + airline_code in the date range
    console.log('Import step 2a: Deleting existing cache rows for pattern IDs:', upsertedPatternIds.length);
    const { error: deleteError } = await supabase
      .from('flight_daily_cache')
      .delete()
      .gte('dep_date', formatDateStr(rangeStart))
      .lte('dep_date', formatDateStr(rangeEnd))
      .in('pattern_id', upsertedPatternIds as any);

    if (deleteError) {
      console.error('Cache cleanup failed:', deleteError);
      throw new Error(`Cache cleanup failed: ${deleteError.message}`);
    }

    // Fetch patterns for cache generation
    console.log('Import step 2b: Fetching patterns for cache generation');
    const { data: patterns, error: fetchError } = await supabase
      .from('flight_patterns')
      .select('id, flight_number, dep_airport, dep_time, arr_airport, arr_time, days_bitmask, aircraft_type, notes')
      .eq('schedule_version', scheduleVersion)
      .eq('airline_code', airlineCode);

    if (fetchError) {
      console.error('Failed to fetch patterns:', fetchError);
      throw new Error(`Failed to fetch patterns: ${fetchError.message}`);
    }
    console.log('Import step 2b: Fetched', (patterns || []).length, 'patterns');

    // Generate cache rows
    const cacheRows: any[] = [];
    const totalDays = Math.ceil((rangeEnd.getTime() - rangeStart.getTime()) / (1000 * 60 * 60 * 24));

    for (const pattern of (patterns || [])) {
      for (let d = 0; d <= totalDays; d++) {
        const currentDate = addDays(rangeStart, d);
        const dayBit = getDayBit(currentDate);

        if (!(pattern.days_bitmask & (1 << dayBit))) continue;

        const depDateStr = formatDateStr(currentDate);
        const depTime = pattern.dep_time as string | null;
        const arrTime = pattern.arr_time as string | null;

        // Skip cache row generation if both times are null
        if (!depTime && !arrTime) continue;

        const isOvernight = (depTime && arrTime) ? arrTime < depTime : false;
        const arrDate = isOvernight ? addDays(currentDate, 1) : currentDate;
        const arrDateStr = formatDateStr(arrDate);

        cacheRows.push({
          pattern_id: pattern.id,
          flight_number: pattern.flight_number,
          dep_airport: pattern.dep_airport,
          arr_airport: pattern.arr_airport,
          dep_date: depDateStr,
          dep_datetime: depTime ? toUtcDatetime(depDateStr, depTime) : null,
          arr_date: arrDateStr,
          arr_datetime: arrTime ? toUtcDatetime(arrDateStr, arrTime) : null,
          aircraft_type: pattern.aircraft_type,
          notes: pattern.notes,
          is_overnight: isOvernight,
        });
      }
    }

    console.log('Import step 2c: Generated', cacheRows.length, 'cache rows, inserting in batches');
    // Bulk insert cache rows in batches
    let cacheRowsInserted = 0;
    for (let i = 0; i < cacheRows.length; i += BATCH_SIZE) {
      const batch = cacheRows.slice(i, i + BATCH_SIZE);
      const { error: insertError } = await supabase
        .from('flight_daily_cache')
        .insert(batch as any);

      if (insertError) {
        console.error(`Cache insert failed at batch ${Math.floor(i / BATCH_SIZE) + 1}:`, insertError);
        throw new Error(`Cache insert failed at batch ${Math.floor(i / BATCH_SIZE) + 1}: ${insertError.message}`);
      }
      cacheRowsInserted += batch.length;
    }
    console.log('Import step 2: Complete —', cacheRowsInserted, 'cache rows inserted');

    // ─── Step 3: Log to admin_activity_log ───
    console.log('Import step 3: Logging import activity');
    onProgress({ step: 'logging', message: '⏳ Logging import activity...' });

    const { data: sessionData } = await supabase.auth.getSession();
    const userId = sessionData?.session?.user?.id;

    if (userId) {
      const { error: logError } = await supabase.from('admin_activity_log').insert({
        user_id: userId,
        action: 'schedule_import',
        entity_type: 'flight_patterns',
        new_values: {
          airline_code: airlineCode,
          schedule_version: scheduleVersion,
          schedule_type: scheduleType,
          valid_from: header.valid_from,
          valid_to: header.valid_to,
          patterns_imported: patternsImported,
          cache_rows_generated: cacheRowsInserted,
          ...(handledFlightsIncluded && handledFlightsIncluded.length > 0
            ? { handled_flights_included: handledFlightsIncluded }
            : {}),
        },
      } as any);
      if (logError) {
        console.error('admin_activity_log insert failed:', logError);
      }
    }

    // ─── Step 4: Run cache maintenance ───
    console.log('Import step 4: Calling maintain-flight-cache');
    onProgress({ step: 'maintenance', message: '⏳ Running cache maintenance...' });

    let rowsPruned = 0;
    let maintenanceFailed = false;
    let maintenanceErrorMsg = '';

    try {
      const { data: maintenanceData, error: maintenanceError } = await supabase.functions.invoke('maintain-flight-cache');

      if (maintenanceError) {
        console.error('maintain-flight-cache error:', maintenanceError);
        throw new Error(maintenanceError.message || 'Cache maintenance failed');
      }

      rowsPruned = maintenanceData?.rowsPruned || 0;
      console.log('Import step 4: Complete — pruned', rowsPruned, 'stale rows');
    } catch (maintenanceError: unknown) {
      maintenanceFailed = true;
      maintenanceErrorMsg = maintenanceError instanceof Error ? maintenanceError.message : 'Unknown error';
      console.error('Cache maintenance error:', maintenanceError);
    }

    const finalMessage = maintenanceFailed
      ? `⚠️ Import succeeded but cache maintenance failed: ${maintenanceErrorMsg}\n\n✅ ${patternsImported} patterns saved, ${cacheRowsInserted} cache rows generated`
      : `✅ Import complete — ${patternsImported} patterns saved, ${cacheRowsInserted} cache rows generated, ${rowsPruned} stale rows pruned`;

    console.log('Import complete:', finalMessage);
    const finalProgress: ImportProgress = {
      step: 'done',
      message: finalMessage,
      patternsImported,
      cacheRowsGenerated: cacheRowsInserted,
      rowsPruned,
    };
    onProgress(finalProgress);
    return finalProgress;

  } catch (err: unknown) {
    const msg = err instanceof Error ? err.message : 'Unknown error';
    console.error('Import pipeline error:', msg, err);
    const errorProgress: ImportProgress = {
      step: 'error',
      message: `❌ ${msg}`,
    };
    onProgress(errorProgress);
    return errorProgress;
  }
}
